// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.AddUserGrid.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * AddUserGrid component
 */

import React, { Component, PropTypes } from 'react';
import connect from 'share/enhancers/Izenda.Connect';

import { ADD_USER_TABLE_DEF as tableDef, SEARCH_OPTIONS } from 'logic/data/Izenda.UserAccountConstants';
import OverflowText from 'share/components/controls/Izenda.OverflowText';
import DataGridSearch from 'share/components/controls/DataGrid/Izenda.DataGridSearch';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import Alert from 'share/components/controls/Izenda.Alert';
import { find, join, map, filter, isEmpty, forEach, merge, values } from 'lodash/fp';

@connect()
class AddUserGrid extends Component {
  static propTypes = {
    store: PropTypes.object,
    selectedItems: PropTypes.array
  };

  constructor(props) {
    super(props);
    const { store, store: { model } } = props;

    this.checkDirty = model.checkDirty.bind(model);
    this.goToPage = store.goToPage.bind(store);
    this.onPageSizeChange = store.onPageSizeChange.bind(store);
    this.updateSearchCriteria = store.updateSearchCriteria.bind(store);
    this.search = store.search.bind(store);
  }

  /**
   * Handle when sort grid
   */
  onSortChange = (): void => {
    const store = this.props.store;

    this.props.confirmDirtyForm(this.checkDirty()).then(() => {
      store.onSortChange();
    });
  };

  /**
   * Render select cell
   */
  renderSelectCell = (data: any, key: any): any => {
    const type = this.props.singleUser ? 'radio' : 'checkbox';
    const { selectedItems, store: { model } } = this.props;

    const onChange = function(e) {
      if (this.props.singleUser) {
        model.resetAllSelected();
      }
      data.updateSelected(e.target.checked);
    };

    // Check item in selected items, check it as default
    const selected = find({ emailAddress: data.emailAddress }, selectedItems);

    return <input type={type} value={data[key]} checked={selected || data[key]} onChange={onChange.bind(this)} />;
  };

  /**
   * Render text cell
   */
  renderTextCell = (data: any, key: any): any => {
    const text = data[key] || '';
    return <OverflowText tooltip={text} text={text} />;
  };

  /**
   * Render roles cell
   */
  renderRoleCell = (data: any, key: any): any => {
    const text = data[key] || [];
    const value = join(', ', map(item => item.name, filter(item => !isEmpty(item), text)));

    return <OverflowText tooltip={value} text={value} />;
  };

  /**
   * Check all
   */
  checkAll = e => {
    const store = this.props.store || {};
    const model = store.model;
    const value = model.userAccountList || [];

    forEach(item => {
      item.updateSelected(e.target.checked);
    }, value);
  };

  /**
   * Render table component
   */
  renderTable = (): any => {
    const { localization, store: { model, isLoadingData } } = this.props;

    return (
      <Table
        dataSource={model.userAccountList}
        onSortChange={this.onSortChange}
        draggableTable={false}
        sortInfo={model.pageInfo.colSortDirs}
        isLoading={isLoadingData}
        loadingText={localization.getText('USER_MESSAGE_LOADING_TEXT')}
      >
        {this.renderColumns()}
      </Table>
    );
  };

  renderColumns = (): any => {
    const { store: { model: { hasSelectedAllUser } } } = this.props;

    const checkBoxSelectAllProps = {
      checked: hasSelectedAllUser,
      onChange: this.checkAll,
      type: 'checkbox'
    };

    const selectRowDefinition = this.props.singleUser
      ? tableDef['selected']
      : merge(
          tableDef['selected'],
          {
            headerComponent: Input,
            headerComponentProps: checkBoxSelectAllProps
          },
          {}
        );

    return map(item => {
      switch (item.key) {
        case 'selected':
          return <Column {...selectRowDefinition} cell={this.renderSelectCell} />;
        case 'roles':
          return <Column {...item} cell={this.renderRoleCell} />;
        default:
          return <Column {...item} cell={this.renderTextCell} />;
      }
    }, values(tableDef));
  };

  /**
   * Render component
   */
  render() {
    const { store: { model: { pageInfo }, alertMessage } } = this.props;

    return (
      <GridLayout>
        <GridLayout.Row>
          <GridLayout.Col cols="12">
            <Alert type={alertMessage.type}>{alertMessage.message}</Alert>
          </GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols="12">
            <DataGridSearch
              options={SEARCH_OPTIONS}
              value={pageInfo.searchProperty}
              onSearch={this.search}
              onSearchPropertiesChange={this.updateSearchCriteria}
            />
          </GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols="12">{this.renderTable()}</GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols="12">
            <Paginator
              onChange={this.goToPage}
              onDropdownChange={this.onPageSizeChange}
              totalRecords={pageInfo.totalRecords}
              pageSize={pageInfo.pageSize}
              pageIndex={pageInfo.pageIndex}
              isDirty={this.checkDirty}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  }
}

export default AddUserGrid;



// WEBPACK FOOTER //
// ./ui/modules/User/Izenda.AddUserGrid.js